import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "../lib/api";
import { Map as MapIcon, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const PT_CENTER = [39.6, -8.0];
const PT_BOUNDS = L.latLngBounds([36.9, -9.6], [42.2, -6.1]);
const STORAGE_KEY = "f2j_results_map_open";

function FitView({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data) return;
    let bounds = null;
    if (data.origin && data.radius_km) {
      bounds = L.latLng(data.origin.lat, data.origin.lng).toBounds(data.radius_km * 2000);
    } else if (data.points && data.points.length) {
      bounds = L.latLngBounds(data.points.map((p) => [p.lat, p.lng]));
      if (data.origin) bounds.extend([data.origin.lat, data.origin.lng]);
    }
    if (!bounds || !bounds.isValid()) bounds = PT_BOUNDS;
    map.fitBounds(bounds.pad(0.08), { maxZoom: 11, animate: true });
  }, [data, map]);
  return null;
}

function InvalidateOnShow({ open }) {
  const map = useMap();
  useEffect(() => { if (open) setTimeout(() => map.invalidateSize(), 50); }, [open, map]);
  return null;
}

/**
 * Mapa interativo (Leaflet + OpenStreetMap) da área pesquisada.
 * - Modo raio: círculo com o raio em km à volta do concelho de referência.
 * - Bolhas por concelho com o nº de vagas para os filtros atuais.
 *
 * Props:
 *  - filters: objeto com os mesmos parâmetros enviados a /api/jobs
 *  - onSelectConcelho?: (point) => void — clique numa bolha
 */
export function ResultsMap({ filters, onSelectConcelho }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== "0"; } catch { return true; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = useMemo(() => JSON.stringify(filters || {}), [filters]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open]);

  useEffect(() => {
    if (!open || !filters) return;
    let alive = true;
    setLoading(true);
    const params = { ...filters };
    delete params.limit; delete params.offset; delete params.sort;
    api.get("/jobs/map", { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ origin: null, radius_km: null, points: [], total: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, open]);

  const maxCount = Math.max(1, ...((data?.points || []).map((p) => p.count)));
  const radius = (n) => 5 + Math.sqrt(n / maxCount) * 18;

  const caption = data?.origin && data?.radius_km
    ? `${data.total} vaga${data.total !== 1 ? "s" : ""} até ${data.radius_km} km de ${data.origin.concelho}`
    : data ? `${data.total} vaga${data.total !== 1 ? "s" : ""} em ${data.points.length} concelho${data.points.length !== 1 ? "s" : ""}` : "";

  return (
    <div data-testid="results-map" className="bg-white rounded-2xl border border-black/5 overflow-hidden">
      <button type="button" data-testid="results-map-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <MapIcon className="w-4 h-4 text-sky-600" /> Mapa da pesquisa
          {open && caption && <span data-testid="results-map-caption" className="font-normal text-slate-500 text-xs">· {caption}</span>}
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="relative h-[280px] sm:h-[340px] border-t border-black/5 z-0">
          <MapContainer center={PT_CENTER} zoom={6} scrollWheelZoom={false} className="h-full w-full" attributionControl>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <InvalidateOnShow open={open} />
            <FitView data={data} />
            {data?.origin && data?.radius_km && (
              <Circle center={[data.origin.lat, data.origin.lng]} radius={data.radius_km * 1000}
                pathOptions={{ color: "#0284c7", weight: 2, fillColor: "#38bdf8", fillOpacity: 0.12, dashArray: "6 6" }} />
            )}
            {(data?.points || []).map((p) => (
              <CircleMarker key={`${p.district}-${p.concelho}`} center={[p.lat, p.lng]} radius={radius(p.count)}
                pathOptions={{ color: "#b45309", weight: 1.5, fillColor: "#f59e0b", fillOpacity: 0.55 }}
                eventHandlers={onSelectConcelho ? { click: () => onSelectConcelho(p) } : undefined}>
                <Tooltip direction="top" offset={[0, -4]}>
                  <span className="font-semibold">{p.concelho}</span> · {p.count} vaga{p.count !== 1 ? "s" : ""}
                  {p.distance_km != null ? ` · ${p.distance_km} km` : ""}
                </Tooltip>
              </CircleMarker>
            ))}
            {data?.origin && (
              <CircleMarker center={[data.origin.lat, data.origin.lng]} radius={7}
                pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#0f172a", fillOpacity: 1 }}>
                <Tooltip permanent direction="right" offset={[8, 0]}>
                  <span className="font-semibold">{data.origin.concelho}</span>
                </Tooltip>
              </CircleMarker>
            )}
          </MapContainer>
          {data && data.points.length === 0 && !loading && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center pointer-events-none">
              <span className="bg-white/90 rounded-full px-4 py-2 text-xs font-semibold text-slate-600 shadow">Sem vagas nesta área</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ResultsMap;
